import { useState, useEffect } from 'react';

export type Tenant = {
  id: string; name: string; slug: string; plan_code: string; status: string;
  max_users: number; max_active_contracts: number; storage_quota_mb: number; storage_used_mb: number;
  custom_domain: string | null; custom_domain_enabled: boolean; custom_domain_status: string;
  created_at: string; updated_at: string;
};

const PLAN_LIMITS: Record<string, { max_users: number; max_active_contracts: number; storage_quota_mb: number }> = {
  free:       { max_users: 1,   max_active_contracts: 10,   storage_quota_mb: 512 },
  pro:        { max_users: 5,   max_active_contracts: 100,  storage_quota_mb: 5120 },
  enterprise: { max_users: 999, max_active_contracts: 9999, storage_quota_mb: 51200 },
};

const STATUSES = [
  { value: 'active',    label: 'Active',    color: 'text-emerald-600' },
  { value: 'trial',     label: 'Trial',     color: 'text-sky-600' },
  { value: 'suspended', label: 'Suspended', color: 'text-amber-600' },
  { value: 'cancelled', label: 'Cancelled', color: 'text-red-600' },
];

const PLANS = ['free', 'pro', 'enterprise'];

interface Props {
  tenant: Tenant | null;
  isNew: boolean;
  onClose: () => void;
  onSave: (data: Partial<Tenant> & { name: string; slug: string }) => void;
}

const slugify = (str: string) => str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
const emptyForm = () => ({ name: '', slug: '', plan_code: 'free', status: 'trial', max_users: 1, max_active_contracts: 10, storage_quota_mb: 512 });

export default function TenantModal({ tenant, isNew, onClose, onSave }: Props) {
  const [form, setForm]             = useState(emptyForm());
  const [slugTouched, setSlugTouched] = useState(false);

  useEffect(() => {
    if (tenant && !isNew) {
      setForm({ name: tenant.name, slug: tenant.slug, plan_code: tenant.plan_code, status: tenant.status, max_users: tenant.max_users, max_active_contracts: tenant.max_active_contracts, storage_quota_mb: tenant.storage_quota_mb });
      setSlugTouched(true);
    } else {
      setForm(emptyForm());
      setSlugTouched(false);
    }
  }, [tenant, isNew]);

  const handleNameChange = (val: string) => setForm((prev) => ({ ...prev, name: val, slug: slugTouched ? prev.slug : slugify(val) }));
  const handlePlanChange = (plan: string) => setForm((prev) => ({ ...prev, plan_code: plan, ...PLAN_LIMITS[plan] }));
  const handleSubmit     = (e: React.FormEvent) => { e.preventDefault(); onSave(form); };
  const storageGB        = (mb: number) => mb >= 1024 ? `${(mb / 1024).toFixed(0)} GB` : `${mb} MB`;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-2xl mx-4 overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-3">
            <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-blue-50 text-blue-600"><i className="ri-building-2-line text-sm"></i></span>
            <h2 className="text-slate-900 font-semibold text-base">{isNew ? 'Nuevo Tenant' : `Editar — ${tenant?.name}`}</h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-all cursor-pointer"><i className="ri-close-line"></i></button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto flex-1">
          <div className="grid grid-cols-2 gap-4">

            {/* Name */}
            <div className="col-span-2">
              <label className="text-slate-500 text-xs mb-1.5 block">Nombre del estudio *</label>
              <input value={form.name} onChange={(e) => handleNameChange(e.target.value)} required placeholder="Ej. Lumina Photography Studio"
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 outline-none focus:border-blue-400 transition-colors" />
            </div>

            {/* Slug */}
            <div className="col-span-2">
              <label className="text-slate-500 text-xs mb-1.5 block">Slug <span className="text-slate-400">(URL única del tenant)</span></label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-mono">app/</span>
                <input value={form.slug} onChange={(e) => { setSlugTouched(true); setForm((prev) => ({ ...prev, slug: slugify(e.target.value) })); }} required placeholder="lumina-studio"
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-10 pr-3 py-2.5 text-sm text-slate-900 font-mono placeholder-slate-400 outline-none focus:border-blue-400 transition-colors" />
              </div>
            </div>

            {/* Plan */}
            <div>
              <label className="text-slate-500 text-xs mb-1.5 block">Plan</label>
              <div className="flex gap-2">
                {PLANS.map((p) => (
                  <button key={p} type="button" onClick={() => handlePlanChange(p)}
                    className={`flex-1 py-2 rounded-lg text-xs font-semibold uppercase transition-all cursor-pointer ${form.plan_code === p ? 'bg-blue-600 text-white' : 'bg-slate-50 border border-slate-200 text-slate-500 hover:text-slate-900'}`}>
                    {p}
                  </button>
                ))}
              </div>
            </div>

            {/* Status */}
            <div>
              <label className="text-slate-500 text-xs mb-1.5 block">Status</label>
              <select value={form.status} onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-blue-400 transition-colors cursor-pointer">
                {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>

            {/* Limits */}
            <div className="col-span-2">
              <p className="text-slate-500 text-xs uppercase tracking-wider mb-3 mt-1">Límites del plan <span className="text-slate-400 normal-case ml-1">(auto-set al cambiar plan)</span></p>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Max Usuarios', key: 'max_users' as const, min: 1 },
                  { label: 'Max Contratos', key: 'max_active_contracts' as const, min: 1 },
                  { label: 'Storage (MB)', key: 'storage_quota_mb' as const, min: 128 },
                ].map((f) => (
                  <div key={f.key}>
                    <label className="text-slate-500 text-xs mb-1.5 block">{f.label}</label>
                    <div className="relative">
                      <input type="number" min={f.min} value={form[f.key]}
                        onChange={(e) => setForm((prev) => ({ ...prev, [f.key]: Number(e.target.value) }))}
                        className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-blue-400 transition-colors" />
                      {f.key === 'storage_quota_mb' && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">{storageGB(form.storage_quota_mb)}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Summary */}
            <div className="col-span-2 bg-blue-50 border border-blue-100 rounded-xl p-4">
              <p className="text-blue-700 text-xs font-semibold uppercase tracking-wider mb-2">Resumen del tenant</p>
              <div className="grid grid-cols-4 gap-3 text-xs">
                <div><span className="text-slate-500">Plan</span><p className="text-slate-900 font-medium capitalize mt-0.5">{form.plan_code}</p></div>
                <div><span className="text-slate-500">Status</span><p className="text-slate-900 font-medium capitalize mt-0.5">{form.status}</p></div>
                <div><span className="text-slate-500">Usuarios</span><p className="text-slate-900 font-medium mt-0.5">{form.max_users === 999 ? '∞' : form.max_users}</p></div>
                <div><span className="text-slate-500">Storage</span><p className="text-slate-900 font-medium mt-0.5">{storageGB(form.storage_quota_mb)}</p></div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-5">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-500 hover:text-slate-900 border border-slate-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap">Cancelar</button>
            <button type="submit" className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap">{isNew ? 'Crear Tenant' : 'Guardar cambios'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
